import { useCallback } from 'react';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import { globalToastService } from '@repo/atomic-components';
import { useLazyGetLogoutQuery } from '@/api/auth/getLogout';
import { isUnauthorized } from '@/api/core/retryPolicy';
import { AppRoutes } from '@/constants/appRoutes';
import { DEVICE_PREFERENCE_STORAGE_KEYS } from '@/constants/localStorageKeys';
import { persistor } from '@/store';
import { navigateWithPageLoad } from '@/utils/navigateWithPageLoad';

/** Removes everything the user left in localStorage and sessionStorage. Device preferences stay. */
export const clearUserStorage = () => {
  try {
    Object.keys(localStorage)
      .filter((key) => !DEVICE_PREFERENCE_STORAGE_KEYS.includes(key))
      .forEach((key) => localStorage.removeItem(key));
    sessionStorage.clear();
  } catch {
    // Storage that can't be read (private browsing, a blocked origin) holds nothing to clear.
  }
};

/** Logs out, then removes the user's data from the browser. */
export const useLogout = () => {
  const [triggerGetLogout] = useLazyGetLogoutQuery();

  return useCallback(async () => {
    try {
      await triggerGetLogout({}).unwrap();
    } catch (error) {
      // A 401 means the session had already ended.
      if (!isUnauthorized(error as FetchBaseQueryError)) {
        globalToastService.show({
          closable: true,
          severity: 'error',
          detail: 'Failed to log out. Please try again.',
          life: 6000,
        });
        return;
      }
    }

    // Otherwise redux-persist can write the chat history back before the page unloads.
    persistor.pause();
    await persistor.flush();
    clearUserStorage();
    // Not a route change: the store and the in-memory caches have to go with the old page.
    navigateWithPageLoad(`/${AppRoutes.Login}`);
  }, [triggerGetLogout]);
};
